// CMS không thu tiền trực tiếp: gửi yêu cầu thu tiền thẻ sang máy POS, theo dõi trạng thái và báo khi POS xác nhận
import { useEffect, useState } from 'react';
import { Input, Modal, Select, Spin } from 'antd';
import dayjs from 'dayjs';
import { CheckCircle2, Clock, Send, XCircle } from 'lucide-react';
import { BRANCHES, METHOD_LABEL, branchName } from '../POS/data';
import usePosStore from '../POS/store';
import MoneyInput from '../POS/components/MoneyInput';
import type { PayRequest, TreatmentCard } from './data';
import { installmentRows, outstanding } from './logic';
import useTreatmentStore from './store';
import { posDebtOf, useTreatmentAuth } from './actions';
import PaidModal, { type PaidResult } from './PaidModal';
import { fmtDate, money } from './ui';

/** Nhãn các đợt mà số tiền này trả cho, VD "Đợt 2/3" hoặc "Đợt 2–3/3" */
export function installmentLabel(card: TreatmentCard, amount: number, cfg: Parameters<typeof installmentRows>[1]) {
  const rows = installmentRows(card, cfg).filter(r => r.left > 0);
  let left = amount;
  const covered: number[] = [];
  for (const r of rows) {
    if (left <= 0) break;
    covered.push(r.no);
    left -= r.left;
  }
  const total = card.installments.length;
  if (!covered.length) return `Trả trước`;
  return covered.length === 1 ? `Đợt ${covered[0]}/${total}` : `Đợt ${covered[0]}–${covered[covered.length - 1]}/${total}`;
}

export const pendingRequestOf = (requests: PayRequest[], cardId: string) => requests.find(r => r.cardId === cardId && r.status === 'pending');

export const REQUEST_STATUS: Record<PayRequest['status'], { label: string; cls: string }> = {
  pending: { label: 'Chờ POS thu', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  paid: { label: 'POS đã thu', cls: 'bg-green-50 text-green-700 border-green-200' },
  rejected: { label: 'POS từ chối', cls: 'bg-red-50 text-red-600 border-red-200' },
  cancelled: { label: 'Đã hủy', cls: 'bg-slate-50 text-slate-500 border-slate-200' },
};

export default function PayRequestModal({ card, onClose }: { card: TreatmentCard | null; onClose: () => void }) {
  const cfg = useTreatmentStore(s => s.config);
  const requests = useTreatmentStore(s => s.payRequests);
  const createPayRequest = useTreatmentStore(s => s.createPayRequest);
  const cancelPayRequest = useTreatmentStore(s => s.cancelPayRequest);
  const workingBranch = usePosStore(s => s.workingBranch);
  const { userName } = useTreatmentAuth();
  const [reqId, setReqId] = useState<string | null>(null);
  const [amount, setAmount] = useState(0);
  const [branchId, setBranchId] = useState(workingBranch);
  const [note, setNote] = useState('');
  const [paid, setPaid] = useState<PaidResult | null>(null);
  const [, tick] = useState(0);

  const req = reqId ? requests.find(r => r.id === reqId) : undefined;
  const rows = card ? installmentRows(card, cfg) : [];
  const debt = card ? outstanding(card) : 0;
  const posDebt = card?.source === 'pos' ? posDebtOf(card) : Infinity;
  const max = Math.min(debt, posDebt);

  useEffect(() => {
    if (!card) return;
    const pending = pendingRequestOf(useTreatmentStore.getState().payRequests, card.id);
    setReqId(pending?.id || null);
    const next = rows.find(r => r.left > 0);
    setAmount(Math.min(max, next?.left || debt));
    setBranchId(workingBranch);
    setNote('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id]);

  // Đồng hồ "đã chờ x phút"
  useEffect(() => {
    if (req?.status !== 'pending') return;
    const t = setInterval(() => tick(x => x + 1), 15000);
    return () => clearInterval(t);
  }, [req?.status]);

  if (!card) return <PaidModal result={paid} onClose={() => setPaid(null)} />;

  const send = () => {
    if (amount <= 0 || amount > max) return;
    const r = createPayRequest({ cardId: card.id, amount, installmentLabel: installmentLabel(card, amount, cfg), branchId, note, by: userName });
    if (r) setReqId(r.id);
  };
  const cancel = () => {
    if (!req) return;
    Modal.confirm({
      title: `Hủy yêu cầu ${req.id}?`, content: 'Máy POS sẽ không còn thấy yêu cầu này.', okText: 'Hủy yêu cầu', okButtonProps: { danger: true }, cancelText: 'Không',
      onOk: () => { cancelPayRequest(req.id, userName, 'Người gửi hủy'); setReqId(null); },
    });
  };
  const viewReceipt = () => {
    if (!req?.paymentId) return;
    setPaid({ cardId: card.id, paymentId: req.paymentId, change: req.change || 0, tendered: req.tendered || 0, title: 'POS đã thu tiền thẻ' });
    onClose();
  };

  const footer = !req ? (
    <div className="flex justify-end gap-2">
      <button onClick={onClose} className="h-9 rounded-lg border border-slate-300 px-4 text-sm font-semibold">Hủy</button>
      <button onClick={send} disabled={amount <= 0 || amount > max} className="flex h-9 items-center gap-1.5 rounded-lg bg-[#357f96] px-4 text-sm font-semibold text-white hover:bg-[#2c6b7f] disabled:opacity-40">
        <Send className="h-4 w-4" /> Gửi sang POS
      </button>
    </div>
  ) : req.status === 'pending' ? (
    <div className="flex justify-between gap-2">
      <button onClick={cancel} className="h-9 rounded-lg border border-red-200 px-4 text-sm font-semibold text-red-600 hover:bg-red-50">Hủy yêu cầu</button>
      <button onClick={onClose} className="h-9 rounded-lg border border-slate-300 px-4 text-sm font-semibold">Đóng – báo khi xong</button>
    </div>
  ) : (
    <div className="flex justify-end gap-2">
      {req.status !== 'paid' && <button onClick={() => setReqId(null)} className="h-9 rounded-lg border border-slate-300 px-4 text-sm font-semibold">Gửi lại</button>}
      {req.status === 'paid' && <button onClick={viewReceipt} className="h-9 rounded-lg border border-slate-300 px-4 text-sm font-semibold">Xem / in phiếu thu</button>}
      <button onClick={onClose} className="h-9 rounded-lg bg-[#357f96] px-4 text-sm font-semibold text-white">Xong</button>
    </div>
  );

  return (
    <>
      <PaidModal result={paid} onClose={() => setPaid(null)} />
      <Modal open={!!card} onCancel={onClose} title={`Thu tiền thẻ ${card.id}`} centered width={540} footer={footer} destroyOnHidden>
        <div className="space-y-3 text-sm">
          <div className="rounded-lg bg-[#ecf6f9] p-3">
            <div><b>{card.customerName}</b> · {card.packageName}</div>
            <div>Giá thẻ {money(card.price)}đ · Còn nợ <b className="text-red-600">{money(debt)}đ</b></div>
          </div>

          {!req && <>
            <table className="w-full text-sm">
              <tbody>
                {rows.map(r => (
                  <tr key={r.no} className="border-b border-slate-100">
                    <td className="py-1">Đợt {r.no}</td>
                    <td className="py-1">{fmtDate(r.dueDate)}</td>
                    <td className="py-1 text-right">{money(r.amount)}</td>
                    <td className={`py-1 text-right text-xs ${r.state === 'overdue' ? 'text-red-600' : r.state === 'paid' ? 'text-green-600' : 'text-slate-500'}`}>
                      {r.state === 'paid' ? 'Đã thu' : r.state === 'overdue' ? `Thiếu ${money(r.left)} · trễ ${r.overdueDays} ngày` : `Thiếu ${money(r.left)}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <label className="block">
              <span className="mb-1 block font-medium text-slate-700">Số tiền cần thu <span className="text-red-500">*</span></span>
              <MoneyInput value={amount} onChange={setAmount} max={max} invalid={amount <= 0} />
              <span className="mt-0.5 block text-xs text-slate-500">{amount > 0 ? `${installmentLabel(card, amount, cfg)} · ` : ''}thu ngân có thể sửa số tiền theo thực tế khách đưa</span>
            </label>
            <label className="block">
              <span className="mb-1 block font-medium text-slate-700">Gửi tới máy POS</span>
              <Select className="w-full" value={branchId} onChange={setBranchId} options={BRANCHES.map(b => ({ value: b.id, label: `POS ${b.name}` }))} />
            </label>
            <Input.TextArea rows={2} maxLength={200} value={note} onChange={e => setNote(e.target.value)} placeholder="Ghi chú cho thu ngân (VD: khách chuyển khoản, đang ngồi quầy 2)" />
            <p className="text-xs text-slate-500">Tiền chỉ được ghi nhận vào thẻ khi thu ngân xác nhận đã thu trên máy POS. Bạn sẽ nhận thông báo khi POS xử lý xong.</p>
          </>}

          {req && (
            <div className="rounded-xl border border-slate-200 p-4 text-center">
              {req.status === 'pending' && <>
                <Spin size="large" />
                <div className="mt-3 text-base font-bold text-slate-800">Đang chờ POS {branchName(req.branchId)} thu tiền</div>
                <div className="mt-1 text-slate-500">Yêu cầu <span className="font-mono">{req.id}</span> · {req.installmentLabel} · <b>{money(req.amount)}đ</b></div>
                <div className="mt-1 flex items-center justify-center gap-1 text-xs text-slate-400"><Clock className="h-3.5 w-3.5" /> Gửi lúc {dayjs(req.createdAt).format('HH:mm')} · đã chờ {Math.max(0, dayjs().diff(dayjs(req.createdAt), 'minute'))} phút</div>
              </>}
              {req.status === 'paid' && <>
                <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
                <div className="mt-2 text-base font-bold text-green-700">POS đã thu {money(req.amountPaid || req.amount)}đ</div>
                <div className="mt-1 text-slate-500">{req.installmentLabel} · {req.method ? METHOD_LABEL[req.method] : ''}{req.txnRef ? ` (${req.txnRef})` : ''} · thu ngân {req.resolvedBy} lúc {req.resolvedAt && dayjs(req.resolvedAt).format('HH:mm')}</div>
                <div className="mt-1 text-slate-500">Còn nợ sau lần thu: <b>{money(outstanding(card))}đ</b></div>
              </>}
              {req.status === 'rejected' && <>
                <XCircle className="mx-auto h-12 w-12 text-red-500" />
                <div className="mt-2 text-base font-bold text-red-600">POS từ chối yêu cầu</div>
                <div className="mt-1 text-slate-500">{req.reason} · {req.resolvedBy} lúc {req.resolvedAt && dayjs(req.resolvedAt).format('HH:mm')}</div>
              </>}
              {req.status === 'cancelled' && <div className="text-slate-500">Yêu cầu đã hủy</div>}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
